import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, api } from "../App";
import DashboardLayout from "../components/layout/DashboardLayout";
import VoiceAgent from "../components/VoiceAgent";
import VendorPortfolio from "../components/VendorPortfolio";
import PermittedActions from "../components/PermittedActions";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { 
  Settings, FileText, Package, IndianRupee, Wrench,
  ArrowRight, AlertCircle, Loader2, CheckCircle2, XCircle, Mic, Crown
} from "lucide-react";
import RefNumber from "../components/RefNumber";

const VendorDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(true);
  const [showVoiceAgent, setShowVoiceAgent] = useState(false);
  const [subData, setSubData] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchSubscription();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboard = async () => {
    try {
      const response = await api.get("/dashboard/vendor");
      if (response.data.error) {
        setHasProfile(false);
      } else {
        setStats(response.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setHasProfile(false);
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await api.get("/subscriptions/current");
      setSubData(res.data);
    } catch { /* silent */ }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: "status-draft",
      submitted: "status-submitted",
      analyzing: "status-analyzing",
      matching: "status-matching",
      quoted: "status-quoted",
      po_issued: "status-po_issued",
      in_production: "status-in_production",
      completed: "status-completed",
      cancelled: "status-cancelled",
      pending: "status-pending",
      accepted: "status-completed",
      rejected: "status-cancelled"
    };
    return statusMap[status] || "status-draft";
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!hasProfile) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <AlertCircle className="w-16 h-16 text-orange-500 mb-4" />
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            Complete Your Vendor Profile
          </h2>
          <p className="text-slate-500 mb-6 text-center max-w-md">
            To start receiving RFQ matches and submitting quotes, please set up your company profile and add your machines.
          </p>
          <Link to="/vendor/profile">
            <Button className="bg-orange-600 hover:bg-orange-700">
              <Settings className="w-4 h-4 mr-2" /> Setup Profile
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="vendor-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {stats?.vendor_profile?.company_name || "Vendor Dashboard"}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {stats?.vendor_profile?.is_approved ? (
                <span className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle2 className="w-4 h-4" /> Verified Vendor
                </span>
              ) : (
                <span className="flex items-center gap-1 text-sm text-amber-600">
                  <AlertCircle className="w-4 h-4" /> Pending Approval
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => setShowVoiceAgent(true)}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="voice-agent-btn"
            >
              <Mic className="w-4 h-4 mr-2" /> Voice Assistant
            </Button>
            <Link to="/vendor/machines">
              <Button variant="outline" data-testid="manage-machines-btn">
                <Wrench className="w-4 h-4 mr-2" /> Machines
              </Button>
            </Link>
            <Link to="/vendor/profile">
              <Button variant="outline" data-testid="edit-profile-btn">
                <Settings className="w-4 h-4 mr-2" /> Profile
              </Button>
            </Link>
          </div>
        </div>

        {/* Subscription Nudge */}
        {subData && (subData.warnings?.length > 0 || subData.subscription?.plan_id === "free") && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-4" data-testid="subscription-nudge">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Crown className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {subData.warnings?.length > 0
                    ? subData.warnings[0].message
                    : "You're on the Free plan — upgrade to unlock more quotes, machines & visibility"}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Quotes: {subData.usage?.quotes_this_month || 0}/{subData.limits?.quotes_per_month === -1 ? "∞" : subData.limits?.quotes_per_month} · Machines: {subData.usage?.machines || 0}/{subData.limits?.machines === -1 ? "∞" : subData.limits?.machines}
                </p>
              </div>
            </div>
            <Link to="/vendor/subscription">
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap" data-testid="upgrade-btn">
                <Crown className="w-3.5 h-3.5 mr-1.5" /> View Plans
              </Button>
            </Link>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.matched_rfqs || 0}</p>
                  <p className="text-sm text-slate-500">Matched RFQs</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.quotes_submitted || 0}</p>
                  <p className="text-sm text-slate-500">Quotes Sent</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_orders || 0}</p>
                  <p className="text-sm text-slate-500">Total Orders</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_machines || 0}</p>
                  <p className="text-sm text-slate-500">Machines</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Card */}
        <Card className="border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800">
          <CardContent className="py-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm uppercase tracking-wider mb-1">Total Revenue</p>
                <p className="text-4xl font-bold text-white">
                  ₹{stats?.total_revenue?.toLocaleString('en-IN') || "0"}
                </p>
              </div>
              <IndianRupee className="w-16 h-16 text-slate-700" />
            </div>
          </CardContent>
        </Card>
        
        {/* Permitted Quick Actions */}
        <PermittedActions 
          dashboardType="vendor" 
          title="Quick Actions"
          description="Available actions based on your permissions"
          columns={4}
        />

        {/* Portfolio Section */}
        <VendorPortfolio />

        <div className="grid md:grid-cols-2 gap-6">
          {/* Matched RFQs */}
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Incoming RFQs</CardTitle>
              <Link to="/vendor/matched-rfqs" className="text-orange-600 hover:text-orange-700 text-sm font-medium flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_rfqs?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_rfqs.map((rfq) => (
                    <Link
                      key={rfq.rfq_id}
                      to={`/vendor/rfq/${rfq.rfq_id}`}
                      className="block p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`rfq-item-${rfq.rfq_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{rfq.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <RefNumber value={rfq.rfq_number} />
                            <span className="text-sm text-slate-500">
                              {rfq.material_type} • Qty: {rfq.quantity}
                            </span>
                          </div>
                        </div>
                        <span className={`status-badge ${getStatusBadge(rfq.status)}`}>
                          {rfq.status.replace("_", " ")}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No matched RFQs yet</p>
                  {!stats?.vendor_profile?.is_approved && (
                    <p className="text-sm text-amber-600 mt-2">
                      Your profile is pending approval
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Orders */}
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Active Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.recent_orders?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_orders.map((order) => (
                    <Link
                      key={order.order_id}
                      to={`/orders/${order.order_id}`}
                      className="block p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`order-item-${order.order_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">
                            {order.item_name || "Manufacturing Order"}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <RefNumber value={order.order_number} />
                            <span className="text-sm text-slate-500">
                              ₹{order.total_amount?.toLocaleString('en-IN', {minimumFractionDigits: 2})}
                            </span>
                          </div>
                        </div>
                        <span className={`status-badge ${getStatusBadge(order.status)}`}>
                          {order.status.replace("_", " ")}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No orders yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Voice Agent Modal */}
      <VoiceAgent isOpen={showVoiceAgent} onClose={() => setShowVoiceAgent(false)} />
    </DashboardLayout>
  );
};

export default VendorDashboard;
